import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Servicios", href: "#servicios" },
  { label: "Precios", href: "#precios" },
  { label: "Manifiesto", href: "#manifiesto" },
  { label: "Método", href: "#metodo" },
  { label: "Forense", href: "#forense" },
  { label: "Formación", href: "#formacion" },
  { label: "Amenazas", href: "#amenazas" },
  { label: "Contacto", href: "#contacto" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="fixed inset-x-0 top-0 z-50 px-5 py-4 md:px-10"
      data-testid="navbar"
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between rounded-full px-5 py-3 transition-[background,border,box-shadow] duration-500 ${
          scrolled ? "glass glow-cyan" : "border border-transparent"
        }`}
      >
        <a href="#top" className="flex items-center gap-3" data-testid="logo">
          <img src="/cyvr-logo.png" alt="CYVR" className="h-12 w-12 object-contain drop-shadow-[0_0_14px_rgba(0,240,255,0.55)]" />
          <span className="font-head text-xl font-bold tracking-tight text-white">
            CYVR<span className="text-cyber-cyan"> LABS</span>
          </span>
        </a>

        <nav className="hidden items-center gap-5 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="font-mono-tech text-[11px] uppercase tracking-[0.12em] text-white/60 transition-colors duration-300 hover:text-cyber-cyan"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/equipo"
            data-testid="nav-equipo"
            className="font-mono-tech text-[11px] uppercase tracking-[0.12em] text-white/60 transition-colors duration-300 hover:text-cyber-cyan"
          >
            Equipo
          </Link>
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to="/acceso"
            data-testid="nav-access"
            className="rounded-full border border-white/15 px-5 py-2 font-mono-tech text-xs font-bold uppercase tracking-widest text-white/80 transition-colors duration-300 hover:border-cyber-cyan hover:text-cyber-cyan"
          >
            Acceder
          </Link>
          <a
            href="#contacto"
            data-testid="nav-cta"
            className="rounded-full bg-cyber-cyan px-5 py-2 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-105"
          >
            Contrátame
          </a>
        </div>

        <button
          className="text-white lg:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="glass mx-auto mt-3 flex max-w-7xl flex-col gap-4 rounded-2xl p-6 lg:hidden"
          >
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-mono-tech text-sm uppercase tracking-widest text-white/70"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/equipo"
              onClick={() => setOpen(false)}
              data-testid="nav-equipo-mobile"
              className="font-mono-tech text-sm uppercase tracking-widest text-white/70"
            >
              Equipo
            </Link>
            <Link
              to="/acceso"
              onClick={() => setOpen(false)}
              data-testid="nav-access-mobile"
              className="rounded-full bg-cyber-cyan px-5 py-2 text-center font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a]"
            >
              Acceder al panel
            </Link>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
